import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { Box } from "@mui/material";
import type { ColDef, ExcelExportParams, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import {
	TEST_SHACK_REPORT_EXCEL_STYLES,
	TEST_SHACK_REPORT_EXPORT_COLUMN_CAP,
} from "@/pages/products/test-shack/utils/reportExcelExport";

interface TestShackReportExcelExportProps {
	hasRows: boolean;
	getExportParams: () => ExcelExportParams;
	disabled: boolean;
	onError: (error: Error) => void;
}

const EXPORT_COLUMN_DEFS: ColDef[] = Array.from(
	{ length: TEST_SHACK_REPORT_EXPORT_COLUMN_CAP },
	(_value, index) => ({ colId: `col${index}`, field: `col${index}` }),
);

export const TestShackReportExcelExport = ({
	hasRows,
	getExportParams,
	disabled,
	onError,
}: TestShackReportExcelExportProps) => {
	const gridApi = useRef<GridApi | null>(null);
	const exportFrame = useRef<number | null>(null);

	const [isGridReady, setIsGridReady] = useState(false);
	const [isExporting, setIsExporting] = useState(false);

	const exportToExcel = () => {
		if (!gridApi.current || isExporting || !hasRows) return;

		setIsExporting(true);
		exportFrame.current = window.requestAnimationFrame(() => {
			try {
				gridApi.current?.exportDataAsExcel(getExportParams());
			} catch (exportError) {
				onError(
					exportError instanceof Error
						? exportError
						: new Error("Unable to export the Test Shack report"),
				);
			} finally {
				exportFrame.current = null;
				setIsExporting(false);
			}
		});
	};

	useEffect(
		() => () => {
			if (exportFrame.current !== null)
				window.cancelAnimationFrame(exportFrame.current);
		},
		[],
	);

	return (
		<Box aria-live="polite">
			<ExcelButton
				size="small"
				fullWidth={false}
				label={isExporting ? "Exporting…" : "Excel"}
				disabled={disabled || !isGridReady || isExporting || !hasRows}
				handleExportToExcel={exportToExcel}
			/>
			<Box
				aria-hidden="true"
				sx={{
					height: 200,
					left: -10000,
					pointerEvents: "none",
					position: "fixed",
					top: 0,
					visibility: "hidden",
					width: 600,
				}}
			>
				<AgGridReact
					rowData={[]}
					columnDefs={EXPORT_COLUMN_DEFS}
					excelStyles={TEST_SHACK_REPORT_EXCEL_STYLES}
					suppressNoRowsOverlay
					onGridReady={(event) => {
						gridApi.current = event.api;
						setIsGridReady(true);
					}}
				/>
			</Box>
		</Box>
	);
};
